import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiDelete, apiGet, apiPost, apiPut } from "../apiClient";
import "./ChatPage.css";
import "./ChatReactions.css";

const ChatPage = ({ token, onUnreadChange }) => {
  const [participants, setParticipants] = useState([]);
  const [currentMemberId, setCurrentMemberId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [draft, setDraft] = useState("");
  const [message, setMessage] = useState("");
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("taskit.chatUsersCollapsed") === "true");
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("taskit.chatSound") !== "false");
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState(null);
  const endRef = useRef(null);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const loadParticipants = useCallback(async () => {
    try {
      const data = await apiGet("/chat/participants", { token });
      setParticipants(data.participants || []);
      setCurrentMemberId(data.current_member_id);
      onUnreadChange?.(data.total_unread || 0);
      setSelectedId((current) => current || data.participants?.[0]?.member_id || null);
    } catch (error) {
      setMessage(error.message);
    }
  }, [onUnreadChange, token]);

  const loadMessages = useCallback(async (participantId, before = null) => {
    if (!participantId) return;
    try {
      const data = await apiGet(`/chat/conversations/${participantId}/messages${before ? `?before=${before}` : ""}`, { token });
      setMessages((current) => before ? [...(data.messages || []), ...current] : (data.messages || []));
      setNextBefore(data.next_before);
      if (!before) {
        await apiPut(`/chat/conversations/${participantId}/read`, undefined, { token });
        await loadParticipants();
        window.setTimeout(() => endRef.current?.scrollIntoView?.({ behavior: "smooth" }), 0);
      }
    } catch (error) {
      setMessage(error.message);
    }
  }, [loadParticipants, token]);

  useEffect(() => { loadParticipants(); }, [loadParticipants]);
  useEffect(() => { if (selectedId) loadMessages(selectedId); }, [loadMessages, selectedId]);
  useEffect(() => {
    const receive = (event) => {
      const payload = event.detail;
      if (payload?.type === "presence" || payload?.type === "unread_changed") loadParticipants();
      if (payload?.type === "message_reactions_changed") {
        if (selectedIdRef.current) loadMessages(selectedIdRef.current);
        return;
      }
      if (payload?.type !== "new_message") return;
      const incoming = payload.message;
      const partnerId = incoming.sender_member_id === currentMemberId ? incoming.recipient_member_id : incoming.sender_member_id;
      if (partnerId === selectedIdRef.current) {
        setMessages((items) => items.some((item) => item.id === incoming.id) ? items : [...items, incoming]);
        apiPut(`/chat/conversations/${partnerId}/read`, undefined, { token }).then(loadParticipants).catch(() => {});
        window.setTimeout(() => endRef.current?.scrollIntoView?.({ behavior: "smooth" }), 0);
      } else {
        loadParticipants();
      }
    };
    window.addEventListener("taskit:chat-event", receive);
    return () => window.removeEventListener("taskit:chat-event", receive);
  }, [currentMemberId, loadMessages, loadParticipants, token]);

  const send = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || !selectedId) return;
    setDraft("");
    try {
      const sent = await apiPost(`/chat/conversations/${selectedId}/messages`, { body }, { token });
      setMessages((items) => items.some((item) => item.id === sent.id) ? items : [...items, sent]);
      setMessage("");
    } catch (error) {
      setDraft(body);
      setMessage(error.message);
    }
  };

  const toggleCollapsed = () => setCollapsed((value) => {
    localStorage.setItem("taskit.chatUsersCollapsed", String(!value));
    return !value;
  });
  const toggleSound = () => setSoundEnabled((value) => {
    localStorage.setItem("taskit.chatSound", String(!value));
    return !value;
  });
  const toggleReaction = async (chatMessage, reaction) => {
    const emoji = reaction.emoji;
    try {
      const result = reaction.reacted_by_current_user
        ? await apiDelete(`/chat/messages/${chatMessage.id}/reactions/${encodeURIComponent(emoji)}`, { token })
        : await apiPut(`/chat/messages/${chatMessage.id}/reactions/${encodeURIComponent(emoji)}`, undefined, { token });
      setMessages((items) => items.map((item) => item.id === chatMessage.id ? { ...item, reactions: result.reactions || [] } : item));
      setReactionPickerMessageId(null);
    } catch (error) {
      setMessage(error.message);
    }
  };
  const selected = participants.find((participant) => participant.member_id === selectedId);

  return <div className={`chat-page ${collapsed ? "users-collapsed" : ""}`}>
    <aside className="chat-users">
      <div className="chat-users-header">
        {!collapsed && <div><strong>Messages</strong><small>{participants.length} participant{participants.length === 1 ? "" : "s"}</small></div>}
        <button onClick={toggleCollapsed} aria-label={collapsed ? "Expand chat users" : "Collapse chat users"}>{collapsed ? "›" : "‹"}</button>
      </div>
      <div className="chat-user-list">
        {participants.map((participant) => <button
          key={participant.member_id}
          className={`chat-user ${selectedId === participant.member_id ? "selected" : ""}`}
          onClick={() => setSelectedId(participant.member_id)}
          title={collapsed ? participant.display_name : undefined}
        >
          <Avatar participant={participant} />
          {!collapsed && <span className="chat-user-copy"><strong>{participant.display_name}</strong><small>{participant.online ? "Online" : participant.email}</small></span>}
          {!!participant.unread_count && <span className="chat-unread">{participant.unread_count}</span>}
        </button>)}
      </div>
      <button className="chat-sound" onClick={toggleSound} title={soundEnabled ? "Mute message sounds" : "Enable message sounds"}>{soundEnabled ? "🔊" : "🔇"}{!collapsed && <span>{soundEnabled ? "Sounds on" : "Sounds off"}</span>}</button>
    </aside>
    <section className="chat-conversation">
      {!selected ? <div className="chat-empty"><h2>No one else is here yet</h2><p>Invite another user and grant access to this workstream to start chatting.</p></div> : <>
        <header className="chat-conversation-header"><Avatar participant={selected} /><div><h1>{selected.display_name}</h1><span>{selected.online ? "Online" : selected.email}</span></div></header>
        {message && <div className="chat-error">{message}</div>}
        <div className="chat-history">
          {nextBefore && <button className="chat-load-older" onClick={() => loadMessages(selectedId, nextBefore)}>Load older messages</button>}
          {!messages.length && <div className="chat-start">This is the beginning of your conversation with {selected.display_name}.</div>}
          {messages.map((item) => <div key={item.id} className={`chat-message ${item.sender_member_id === currentMemberId ? "mine" : "theirs"}`}>
            <div className="chat-message-body">{item.body}</div><time>{formatTime(item.created_at)}</time>
            <div className="chat-reactions">
              {(item.reactions || []).map((reaction) => <button key={reaction.emoji} className={reaction.reacted_by_current_user ? "mine" : ""} title={reaction.participants?.join(", ")} aria-label={`${reaction.emoji} reaction, ${reaction.count}`} onClick={() => toggleReaction(item, reaction)}>{reaction.emoji} <span>{reaction.count}</span></button>)}
              <button className="chat-add-reaction" aria-label={`Add reaction to message ${item.id}`} onClick={() => setReactionPickerMessageId((current) => current === item.id ? null : item.id)}>☺</button>
              {reactionPickerMessageId === item.id && <div className="chat-emoji-picker">{REACTION_EMOJIS.map((emoji) => {
                const existing = (item.reactions || []).find((reaction) => reaction.emoji === emoji);
                return <button key={emoji} aria-label={`React with ${emoji}`} onClick={() => toggleReaction(item, existing || { emoji, reacted_by_current_user: false })}>{emoji}</button>;
              })}</div>}
            </div>
          </div>)}
          <div ref={endRef} />
        </div>
        <form className="chat-composer" onSubmit={send}>
          <textarea aria-label="New message" value={draft} maxLength={4000} placeholder={`Message ${selected.display_name}`} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); send(event); }
          }} />
          <button type="submit" disabled={!draft.trim()}>Send</button>
        </form>
      </>}
    </section>
  </div>;
};

const Avatar = ({ participant }) => <span className="chat-avatar">
  {participant.avatar_url ? <img src={participant.avatar_url} alt="" /> : initials(participant.display_name)}
  <i className={participant.online ? "online" : ""} />
</span>;
const initials = (name = "") => name.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "?";
const formatTime = (value) => new Date(value).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
const REACTION_EMOJIS = ["👍", "❤️", "😂", "🎉", "👀", "✅", "🚀"];

export default ChatPage;
